import { useState, useRef, useEffect, useMemo } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const norm = (t) => (t || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export function SearchableSelect({ items, value, onSelect, placeholder = "Selectionnez...", searchPlaceholder = "Rechercher...", testId, className, maxRender = 200 }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const selectedItem = items.find(i => i.value === value);

  // Recherche sans tenir compte des accents ni de la casse (« uree » trouve « Urée »)
  // Texte normalisé calculé une fois par liste, pas à chaque frappe (catalogues E-Phy de ~800 produits)
  const indexed = useMemo(() => items.map(i => ({ item: i, text: `${norm(i.label)}\n${norm(i.sublabel)}` })), [items]);
  const q = norm(search.trim());
  const filtered = q ? indexed.filter(e => e.text.includes(q)).map(e => e.item) : items;

  // Limite le DOM rendu : si pas de recherche et liste très longue, on rend
  // uniquement les `maxRender` premiers pour rester réactif. L'utilisateur
  // doit taper pour accéder aux autres.
  const display = filtered.slice(0, maxRender);
  const overflow = filtered.length - display.length;

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setSearch("");
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full h-9 justify-between text-left font-normal text-sm px-3", !selectedItem && "text-[#A3A3A3]", className)}
          data-testid={testId}
        >
          <span className="truncate flex-1">
            {selectedItem ? selectedItem.label : placeholder}
          </span>
          <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 z-[9999]" align="start">
        <div className="flex items-center border-b px-3">
          <Search className="mr-2 h-3.5 w-3.5 shrink-0 opacity-50" />
          <input
            ref={inputRef}
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="flex h-9 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            data-testid={testId ? `${testId}-search` : undefined}
          />
          {search && (
            <button onClick={() => setSearch("")} className="text-xs text-muted-foreground hover:text-foreground ml-1">
              &times;
            </button>
          )}
        </div>
        <div ref={listRef} className="max-h-[250px] overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <p className="py-4 text-center text-xs text-muted-foreground">Aucun resultat</p>
          ) : (
            <>
              {display.map(item => (
                <button
                  key={item.value}
                  onClick={() => { onSelect(item.value, item); setOpen(false); }}
                  className={cn(
                    "relative flex w-full cursor-default select-none items-start rounded-sm px-2 py-1.5 text-left outline-none hover:bg-accent hover:text-accent-foreground",
                    value === item.value && "bg-accent"
                  )}
                  data-testid={testId ? `${testId}-item-${item.value.slice(0, 20)}` : undefined}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{item.label}</p>
                    {item.sublabel && (
                      <p className="text-[10px] text-muted-foreground truncate">{item.sublabel}</p>
                    )}
                  </div>
                  {value === item.value && <Check className="h-3.5 w-3.5 shrink-0 mt-0.5 ml-1" />}
                </button>
              ))}
              {overflow > 0 && (
                <p className="py-2 text-center text-[10px] text-muted-foreground italic">
                  +{overflow} autres résultats — affinez votre recherche pour les voir
                </p>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
